Observable, { type ObservableValue } from @danielx/observable

{
  DataStream
  MidiEvent
  MidiFile
  nextTrackEvent
  remove
} from @danielx/lib

Stats from ./stats.civet

// For importing older songs
SongV2 from ../../song-v2.civet
{ defaultPack } from ../../sample.civet

{abs, floor, max, min, round} := Math
microsecondsPerSecond := 1000000

byTick := (a: {tick: number}, b: {tick: number}) =>
  a.tick - b.tick

asciiEncoder := new TextEncoder

metadataDefaultValues :=
  dxpc: 1
  title: undefined
  description: undefined
  authorId: undefined
  loop: false
  channelSprites: undefined
  spriteSha: undefined
  // Only applied to songs with no player meta event of their own — imported MIDI and
  // new songs. Was "Ct4mgm"; that bank is still resolvable by name for songs that
  // already name it, it just isn't what a fresh import reaches for anymore.
  synth: "GeneralUser GS"
  synthSha: undefined
  theme: undefined
  afxPresetName: undefined
  vfxPresetName: undefined
  vfxSource: undefined
  // Per-channel effect chains, buses and a master chain (lib/effect-graph.civet). Set by
  // format importers (scripts/social/os-midi.mjs for Online Sequencer); absent on ordinary songs.
  effects: undefined as import("../effect-graph.civet").EffectGraphSpec | undefined

metadataKeys := Object.keys metadataDefaultValues |> as! (keyof Metadata)[]

mapMetadata := (self: MidiSong, metadata: Metadata, key: keyof Metadata) =>
  o := Observable metadata[key]

  o.observe (v) =>
    //@ts-ignore
    metadata[key] = v
    self.updatePlayerMetaEvent()

  //@ts-ignore
  self[key] = o

// event map is an array of MIDI events sorted by increasing tick
// returns the last event with e.tick <= tick
mappedEventAtTick := <T extends {tick: number}>(eventMap: T[], tick: number) =>
  mappedEvent .= eventMap[0]

  i .= 1
  l .= eventMap.length
  let e
  while i < l and e = eventMap[i++]
    if e.tick <= tick
      mappedEvent = e
    else
      break

  return mappedEvent

defaultTimeSignature :=
  numerator: 4
  denominator: 4

calculateMeasureNumbers := (results: {beat: number, n: number}[], timeSignatureMap: TimeSignature[], songLength: number) =>
  b .= 0
  n .= 0 // measure number
  every := 4 // spacing in measures between measure number markers

  numerator .= 4
  denominator .= 4

  timeSignatureMap.forEach (ts) =>
    {beat} := ts

    if beat > b
      beatLength := numerator * (4 / denominator)
      measures .= (beat - b) / beatLength

      while measures > 1
        measures--
        if n % every is 0
          results.push
            beat: b
            n: n
        n++
        b += beatLength

      if measures > 0
        if n % every is 0
          results.push
            beat: b
            n: n
        n++

      b = beat

    {numerator, denominator} = ts

  beat := songLength
  if beat > b
    beatLength := numerator * (4 / denominator)
    measures .= (beat - b) / beatLength

    while measures > 1
      measures--
      if n % every is 0
        results.push
          beat: b
          n: n
      n++
      b += beatLength

    if measures > 0
      if n % every is 0
        results.push
          beat: b
          n: n
      n++

  return results

dxpcMetaEvent := /^{\s*"dxpc"/

type TimeSignature =
  beat: number
  numerator: number
  denominator: number
  event: MidiEvent
  set: (numerator: number, denominator: number) => void
  tick: number

type Metadata = typeof metadataDefaultValues

type GetterSetter<T> =
  (): T
  (value: T): void

type ChannelSprites = GetterSetter<{[channel: number]: number}>

export class MidiSong
  //@ts-ignore
  afxPresetName: ObservableValue<string?>
  //@ts-ignore
  dxpc: ObservableValue<number?>
  //@ts-ignore
  description: ObservableValue<string?>
  //@ts-ignore
  authorId: ObservableValue<string?>
  //@ts-ignore
  loop: ObservableValue<boolean>
  //@ts-ignore
  spriteSha: ObservableValue<string?>
  //@ts-ignore
  synth: ObservableValue<string?>
  //@ts-ignore
  synthSha: ObservableValue<string?>
  //@ts-ignore
  theme: ObservableValue<string?>
  //@ts-ignore
  title: ObservableValue<string?>
  //@ts-ignore
  vfxPresetName: ObservableValue<string?>
  //@ts-ignore
  vfxSource: ObservableValue<string?>

  defaultBeatLength := 64

  ticksPerBeat = 96
  events: MidiEvent[] = []

  markers: MidiEvent[] := []
  tempoMap: MidiEvent[] := []
  timeSignatureMap: TimeSignature[] := []
  measureNumbers: {beat: number, n: number}[] := []
  keySignatureMap: MidiEvent[] := []
  playerMeta := {
    dxpc: 1
  } as Metadata
  channelSprites: ChannelSprites = () => []
  playerMetaEvent: MidiEvent?
  _instrumentMap: Map<number, number> := new Map

  channelActive := Array 16
  channelBanks := Array 16
  channelPans := Array 16
  // hold initial channel volume metadata
  // channel number => midi volume value
  channelVolumes := Array 16

  // Add a time signature midi event to the time signature map
  _addTimeSignature := (e: MidiEvent) =>
    {tick, numerator, denominator} := e
    beat := tick / @ticksPerBeat

    timeSignatureMapChanged := @timeSignatureMapChanged

    ts := {
      // Round to nearest 128th note
      beat: round(beat * 32) / 32
      numerator
      denominator
      event: e
      set: (numerator: number, denominator: number) ->
        @numerator = @event.numerator = numerator
        @denominator = @event.denominator = denominator
        timeSignatureMapChanged.increment()
      tick: tick
    }

    @timeSignatureMap.push ts
    @timeSignatureMap.sort byTick

  metadataEventHandler := (e: MidiEvent) =>
    { tick, type } := e

    switch type
      when "controlChange"
        { c, n, v } := e
        if tick is 0
          switch n
            when 0x00 // bank select
              @channelBanks[c] = v
            when 0x07 // volume
              @channelVolumes[c] = v
            when 0x0A // pan
              @channelPans[c] = v

      when "endOfTrack"
        @beatLength tick / @ticksPerBeat

      when "text"
        { text } := e
        if tick is 0 and text!.match dxpcMetaEvent
          try
            data := JSON.parse text!
            @playerMetaEvent = e

            metadataKeys.forEach (prop) =>
              @[prop](data[prop])

          catch e
            console.error e

      when "marker"
        @markers.push e

      when "programChange"
        { c, n } := e
        // Eventually this will track per channel programs across time but we're
        // sticking to one instrument per channer for now.
        if !@instrumentMap.has(c)
          @instrumentMap.set(c, n)

      when "setTempo"
        @tempoMap.push e

      when "keySignature"
        @keySignatureMap.push e

      when "timeSignature"
        @_addTimeSignature e

  resetMetadata := =>
    @markers.length = 0
    @tempoMap.length = 0
    @timeSignatureMap.length = 0
    @measureNumbers.length = 0
    @keySignatureMap.length = 0
    @playerMetaEvent = undefined

    @channelActive.fill(true)
    @channelBanks.fill(0)
    @channelBanks[9] = 128
    @channelPans.fill(64)
    @channelVolumes.fill(100)

    @instrumentMap.clear()

  ensureMetadata := =>
    addedEvents := []

    // Add default tempo
    // Tempo map must always have a tempo at tick zero
    if @tempoMap.length is 0 or @tempoMap[0].tick != 0
      e := new MidiEvent 0, new Uint8Array [0xff, 0x51, 0x03, 0, 0, 0]
      e.microsecondsPerBeat = 500000
      @tempoMap.unshift e
      addedEvents.push e

    // Add event for default time signature
    if @timeSignatureMap.length is 0
      // 4/4: the denominator byte is a power-of-two exponent, so 4 is 2, not 1.
      e := new MidiEvent 0, new Uint8Array [0xff, 0x58, 0x04, 4, 2, 24, 8]
      @_addTimeSignature e
      addedEvents.push e

    // Add event for default key signature
    if @keySignatureMap.length is 0
      e := new MidiEvent 0, new Uint8Array [0xff, 0x59, 0x02, 0, 0]
      @keySignatureMap.push e
      addedEvents.push e

    unless @playerMetaEvent
      Object.entries(metadataDefaultValues).forEach ([key, value]) =>
        //@ts-ignore
        @[key as keyof Metadata](value)

    // Push the same references into @events so mutations propagate
    // to saved MIDI.
    if addedEvents.length
      addedEvents.forEach (e) =>
        @events.push e
      @sortEvents()

    @tempoMapChanged.increment()
    @timeSignatureMapChanged.increment()
    @instrumentMapChanged.increment()

  recomputeMetadata := =>
    @resetMetadata()
    @events = @events.map (e) =>
      e.copy()
    @events.forEach (midiEvent) =>
      @metadataEventHandler midiEvent
    @ensureMetadata()

  init = (data: Uint8Array) =>
    midi := new MidiFile(data)
    // reset state
    {@ticksPerBeat} = midi
    @resetMetadata()

    // Read all the events and normalize running status
    // store in an array sorted by tick so we can add and remove events when
    // editing
    @events = []
    tick .= 0
    while e := nextTrackEvent(midi)
      // running status
      if e.type != e.data[0]
        // Sized to this event: a fixed 3-byte scratch buffer left a stale
        // third byte on 1-data-byte messages (program change, channel pressure)
        data := new Uint8Array(e.data.length + 1)
        data[0] = e.type
        data.set(e.data, 1)
        e.data = data

      // Advance tick
      tick += e.deltaTime

      midiEvent := new MidiEvent tick, e.data
      @events.push midiEvent

      @metadataEventHandler midiEvent

    @ensureMetadata()

    return @

  getState = =>
    {
      beatLength: @beatLength()
      events: @events.map (e) =>
        e.copy()
      @ticksPerBeat
    }

  restoreState = (state: StateData) =>
    let beatLength
    {beatLength, @events, @ticksPerBeat} = state

    @recomputeMetadata()

    @beatLength beatLength

    return

  deleteRange = (range: SelectionRange) =>
    [{beat:startBeat, note:startNote}, {beat:endBeat, note:endNote}] .= range

    if endBeat < startBeat
      swap .= endBeat
      endBeat = startBeat
      startBeat = swap

    if endNote < startNote
      swap .= endNote
      endNote = startNote
      startNote = swap

    startTick := floor startBeat * @ticksPerBeat
    endTick := floor endBeat * @ticksPerBeat

    held := new Map
    heldInRange := new Map
    @events = @events.filter (e) =>
      {channel, note, tick, type} := e

      return true unless note
      return true unless startNote <= note <= endNote

      if tick < startTick
        // Track held notes from before start of range
        switch type
          when "noteOn"
            k := (channel << 8) + note
            held.set k, (held.get(k) ?? 0) + 1
          when "noteOff"
            k := (channel << 8) + note
            held.set k, (held.get(k) ?? 0) - 1

        return true
      else if tick <= endTick
        switch type
          when "noteOn"
            k := (channel << 8) + note
            heldInRange.set k, (heldInRange.get(k) ?? 0) + 1
            return false
          when "noteOff"
            k := (channel << 8) + note
            v .= (held.get(k) ?? 0) - 1
            held.set k, v
            if v >= 0
              // release from a note held before the range started
              return true
            else
              v = (heldInRange.get(k) ?? 0) - 1
              heldInRange.set k, v
              return !(v >= 0)

      else
        // Ensure trailing noteOffs
        switch type
          when "noteOff"
            k := (channel << 8) + note
            v := (heldInRange.get(k) ?? 0) - 1
            heldInRange.set k, v
            return !(v >= 0)

      return true

    @sortEvents()

  patternFromRange = (range: SelectionRange) =>
    evs := @eventsWithinRange(range)

    if evs.length
      // center around note 60
      noteOffset := evs[0].note!

      pattern :=
        channelPrograms: evs.channelPrograms
        events: evs.map (e) =>
          e = e.copy()

          // Adjust ticks relative to pattern start
          startTick := floor min(range[0].beat, range[1].beat) * @ticksPerBeat
          e.tick -= startTick

          {note} := e
          if note?
            e.note = note + 60 - noteOffset

          return e

  // Returns an array of events annotated with channel => program settings
  eventsWithinRange = (range: SelectionRange) =>
    [{beat:startBeat, note:startNote}, {beat:endBeat, note:endNote}] .= range

    if endBeat < startBeat
      swap .= endBeat
      endBeat = startBeat
      startBeat = swap

    if endNote < startNote
      swap .= endNote
      endNote = startNote
      startNote = swap

    startTick := floor startBeat * @ticksPerBeat
    endTick := floor endBeat * @ticksPerBeat

    channelPrograms := Array(16) as number[]

    held := new Map
    heldInRange := new Map
    evs := @events.filter (e) =>
      {channel, note, tick, type} := e

      return unless channel?

      if type is "programChange"
        channelPrograms[channel] = e.program

      return unless note?
      // skip channel events for inactive channels
      return unless @channelActive[channel]
      return unless startNote <= note <= endNote

      if tick < startTick
        // Track held notes from before start of range
        switch type
          when "noteOn"
            k := (channel << 8) + note
            held.set k, (held.get(k) ?? 0) + 1
          when "noteOff"
            k := (channel << 8) + note
            held.set k, (held.get(k) ?? 0) - 1

        return
      else if tick <= endTick
        switch type
          when "noteOn"
            k := (channel << 8) + note
            heldInRange.set k, (heldInRange.get(k) ?? 0) + 1
            return true
          when "noteOff"
            k := (channel << 8) + note
            v .= (held.get(k) ?? 0) - 1
            held.set k, v
            if v >= 0
              return // release from a note held before the range started
            else
              v = (heldInRange.get(k) ?? 0) - 1
              heldInRange.set k, v
              return v >= 0

      else
        // Ensure trailing noteOffs
        switch type
          when "noteOff"
            k := (channel << 8) + note
            v := (heldInRange.get(k) ?? 0) - 1
            heldInRange.set k, v
            return v >= 0

      return

    Object.assign evs, { channelPrograms }

  instrumentMapChanged = Observable 0

  get instrumentMap()
    @instrumentMapChanged()

    return @_instrumentMap

  // Mapping of channel number -> spritesheet number
  // default/undefined means to use the current channel's program number
  // sprite number means use that sprite for this channel throughout
  spriteForChannel = (c: number) =>
    // observe changes to instrument map
    @instrumentMapChanged()

    cs := @channelSprites()

    let s
    if cs
      s = cs[c]

    if s?
      return s

    @_instrumentMap.get(c)

  setChannelSprite = (c: number, i: number) =>
    mapping := Object.assign {}, @channelSprites()
    mapping[c] = i

    @channelSprites(mapping)

  removeChannelSprite = (c: number) =>
    mapping := Object.assign {}, @channelSprites()
    delete mapping[c]

    @channelSprites(mapping)

  load = (url: string) =>
    await fetch(url)
    |> await .arrayBuffer()
    |> new Uint8Array
    |> @init

  // Convert old JSON to new midi style
  fromJSON = (data: unknown) =>
    songV2 := SongV2()

    //@ts-expect-error
    songV2.loadSettings JSON.parse JSON.stringify defaultPack
    //@ts-expect-error
    songV2.fromJSON(data)
    //@ts-expect-error
    @init songV2.toMidi()

  // Add noteOn and noteOff events
  addNote = (c: number, n: number, v: number, beat: number, duration: number) =>
    return unless 0 <= n < 128

    maxTicks := @tickLength()

    tick := min maxTicks, floor beat * @ticksPerBeat
    endTick := min maxTicks, floor (beat + duration) * @ticksPerBeat

    // note on
    data .= new Uint8Array [ 0x90|(c & 0xf), n, v]
    e .= new MidiEvent tick, data
    @events.push e

    // note off
    data = new Uint8Array [ 0x90|(c & 0xf), n, 0]
    e = new MidiEvent endTick, data
    @events.push e

    @sortEvents()

  // Remove noteOn near position and corresponding noteOff event
  removeNote = (pos: BeatNote, within: number) =>
    candidates := @noteOnEventsNear(pos, within)

    let noteOn, noteOff
    if candidates.length
      noteOn = candidates[0]
      noteOff = @correspondingNoteOff noteOn

    if noteOn and noteOff
      // remove noteOn and noteOff events
      remove @events, noteOn
      remove @events, noteOff
      return true

    return

  addEvent = (e: MidiEvent) =>
    @events.push e.copy()
    @sortEvents()

  addEvents = (evs: MidiEvent[]) =>
    evs.forEach (e) =>
      @events.push e.copy()

    @sortEvents()

  // Remove event that matches predicate function
  removeEvent = (beat: number, within: number, predicate: (e: MidiEvent) => boolean) =>
    tick := beat * @ticksPerBeat
    withinTicks := within * @ticksPerBeat

    candidates := @events.filter (e) =>
      { tick: eventTick } := e
      (tick - withinTicks <= eventTick <= tick + withinTicks) and
      predicate(e)

    if candidates.length
      // Find closest event to tick
      candidates.sort (a, b) =>
        abs(a.tick - tick) - abs(b.tick - tick)

      remove @events, candidates[0]
      @recomputeMetadata()
      return true

    return

  sortEvents = =>
    @events.sort byTick

  // Return the noteOff event corresponding to a noteOn event
  // takes multiple held notes into account
  correspondingNoteOff = (noteOn: MidiEvent) =>
    {channel, note, tick} := noteOn

    // remove noteOn and nth following note off event where n is the number
    // of currently held same notes for the channel

    held .= 0
    i .= 0
    evs := @events
    l := evs.length
    let e, noteOff

    while i < l and e = evs[i++]
      {channel:eventChannel, note:eventNote, tick:eventTick, type} := e

      if note is eventNote and channel is eventChannel
        if eventTick < tick
          switch type
            when "noteOn"
              held += 1
            when "noteOff"
              held -= 1
        else
          if type is "noteOff"
            held -= 1
            if held < 0
              noteOff = e
              break

    return noteOff

  noteOnEventsNear = (pos: BeatNote, within: number) =>
    // midi beat, midi note
    { beat, note } := pos
    tick := beat * @ticksPerBeat
    withinTicks := within * @ticksPerBeat

    @events.filter (e) =>
      { note:eventNote, tick:eventTick, type } := e

      (and)
        type is "noteOn"
        eventNote?
        tick - withinTicks <= eventTick <= tick + withinTicks
        // TODO: also check same staff line to remove sharped or flat notes
        note - 1 <= eventNote <= note + 1

  clear = =>
    @_instrumentMap.forEach (p, c) =>
      @setProgram c, p

    @events = @events.filter ({tick, type}) =>
      tick is 0 and (
        type is 'keySignature' or
        type is 'programChange' or
        type is 'setTempo' or
        type is 'text' or
        type is 'timeSignature'
      )

    @recomputeMetadata()
    @beatLength @defaultBeatLength

  beatAtTime = (targetTime: number) =>
    targetTime *= microsecondsPerSecond // μs
    tempo .= 500000 // μs / beat
    lastTick .= 0
    time .= 0 // in μs

    l := @tempoMap.length
    i .= 0
    let e

    while i < l and e = @tempoMap[i++]
      {tick, tempo:_tempo} := e
      deltaTime := tick - lastTick
      us := deltaTime / (@ticksPerBeat / tempo)

      if time + us <= targetTime
        lastTick = tick
        time += us
        tempo = _tempo
      else
        break

    return lastTick / @ticksPerBeat + (targetTime - time) / tempo

  timeAtBeat = (targetBeat: number) =>
    targetTick := targetBeat * @ticksPerBeat
    tempo .= 500000 // μs / beat
    lastTick .= 0
    time .= 0 // in μs

    l := @tempoMap.length
    i .= 0
    let e

    while i < l and e = @tempoMap[i++]
      {tick, tempo:_tempo} := e
      if tick <= targetTick
        deltaTime := tick - lastTick
        lastTick = tick
        time += tempo / @ticksPerBeat * deltaTime
        tempo = _tempo
      else
        break

    deltaTime := targetTick - lastTick
    if deltaTime > 0
      time += tempo / @ticksPerBeat * deltaTime

    return time / microsecondsPerSecond

  tempoAtBeat = (b: number) =>
    tempo .= 500000
    i .= 0
    tick := b * @ticksPerBeat
    evs := @events
    l := evs.length
    let e
    while i < l and e = evs[i++]
      if e.tick <= tick
        if t := e.microsecondsPerBeat
          tempo = t

    return tempo

  tickAtTime = (s: number) =>
    floor @beatAtTime(s) * @ticksPerBeat

  setBank = (c: number, b: number) =>
    c = c & 0xf
    b = b & 0x7f

    BANK_SELECT := 0x0

    // Find first volume CC event in this channel
    e := @events.find ({data}) =>
      data[0] is (0xb0 + c) and data[1] is BANK_SELECT

    if e
      e.tick = 0
      e.data[2] = b
    else
      e := new MidiEvent 0, new Uint8Array [0xb0 + c, BANK_SELECT, b]
      @events.push e

    @sortEvents()

    @channelBanks[c] = b

    return

  setProgram = (c: number, p: number) =>
    c = c & 0xf
    p = p & 0x7f

    // Find first program change event in this channel
    i .= 0
    evs := @events
    l := evs.length
    let e
    while i < l and e = evs[i++]
      if e.channel is c and e.type is "programChange"
        break

    if e
      e.tick = 0
      e.program = p
    else // If no program change event found in this channel then add one
      e := new MidiEvent 0, new Uint8Array [0xc0 + c, p]
      @events.push e

    @sortEvents()

    @_instrumentMap.set(c, p)
    @instrumentMapChanged.increment()

    return

  setVolume = (c: number, v: number) =>
    c = c & 0xf
    v = v & 0x7f
    VOL := 0x7

    // Find first volume CC event in this channel
    e := @events.find ({data}) =>
      data[0] is (0xb0 + c) and data[1] is VOL

    if e
      e.tick = 0
      e.data[2] = v
    else
      e := new MidiEvent 0, new Uint8Array [0xb0 + c, VOL, v]
      @events.push e

    @sortEvents()

    @channelVolumes[c] = v

    return

  setPan = (c: number, v: number) =>
    c = c & 0xf
    v = v & 0x7f
    PAN := 0xA

    // Find first pan CC event in this channel
    e := @events.find ({data}) =>
      data[0] is (0xb0 + c) and data[1] is PAN

    if e
      e.tick = 0
      e.data[2] = v
    else
      e := new MidiEvent 0, new Uint8Array [0xb0 + c, PAN, v]
      @events.push e

    @sortEvents()

    @channelPans[c] = v

    return

  eventsWithinBeats = (start: number, end: number) =>
    @events.filter ({tick}) =>
      beat := tick / @ticksPerBeat

      start <= beat < end

  eventsWithinTime = (start: number, end: number) =>
    startTick := @tickAtTime start
    endTick := @tickAtTime end
    @events.filter ({tick}) =>
      startTick <= tick < endTick

  // Iterator used by player-audio to keep track of elapsed seconds and pass
  // events to channel-data, respecting tempo changes
  eventIterator = =>
    tempo .= 500000 // μs / beat
    currentTick .= 0
    time .= 0
    i .= 0

    handleEvent := (handler: (e: MidiEvent) => void, event: MidiEvent, tick: number, dt: number): void =>
      c := event.channel
      // The cursor moves past the event whether or not the handler survives it. Advancing
      // only after a successful handler call meant an event whose scheduling threw (an
      // AudioParam rejecting a value, a missing worklet) was retried on every scheduler
      // tick, throwing every time: the beat froze there and the rest of the song never
      // played. The error still propagates — the caller sees it once per bad event — but
      // the next call carries on from the following event.
      try
        if !c? or @channelActive[c]
          handler(event)
      finally
        currentTick = tick
        time += dt
        i++

        // Update tempo
        if t := event.microsecondsPerBeat
          tempo = t

    // Consume events until the first noteOn event, this will handle tuning,
    // program changes, etc. so that the synth is ready when the song loads
    // but before the audio is played once.
    return {
      consumeUntilNoteOn: (handler: (e: MidiEvent) => void) =>
        l := @events.length
        let event

        while i < l && event = @events[i]
          {tick, type} := event
          dt := tempo / @ticksPerBeat * (tick - currentTick) / microsecondsPerSecond

          if type != 'noteOn'
            handleEvent(handler, event, tick, dt)
          else
            break

        return

      consumeUntilTime: (endTime: number, handler: (e: MidiEvent) =>) =>
        l := @events.length

        let event
        while i < l && event = @events[i]
          { tick } := event
          dt := tempo / @ticksPerBeat * (tick - currentTick) / microsecondsPerSecond

          if time + dt < endTime
            handleEvent(handler, event, tick, dt)
          else
            break

        return
    }

  beatLength = Observable @defaultBeatLength
  // length in beats
  length = =>
    @beatLength()

  tickLength = =>
    @beatLength() * @ticksPerBeat

  secondsLength = =>
    @timeAtBeat @beatLength()

  // TODO: redo settings, use dxpc custom meta event
  settings = => []

  stats = =>
    Stats @events

  tempoEventAtBeat = (beat: number) =>
    @tempoEventAtTick beat * @ticksPerBeat

  tempoEventAtTick = (tick: number) =>
    mappedEventAtTick(@tempoMap, tick)

  addSetTempoEvent = (beat: number) =>
    tick := floor beat * @ticksPerBeat

    existingEvent := @tempoEventAtTick(tick)
    if existingEvent.tick is tick
      return false

    e := new MidiEvent tick,
      new Uint8Array [0xff, 0x51, 0x03, 0, 0, 0]

    // Set to existing tempo value
    { microsecondsPerBeat } := existingEvent
    e.microsecondsPerBeat = microsecondsPerBeat

    @tempoMap.push e
    @tempoMap.sort byTick
    @events.push e
    @sortEvents()

    @tempoMapChanged.increment()

    return true

  // Set the tempo in force at `beat` — the most recent setTempo event at or
  // before it, which is the beat-0 event until a change is inserted. Editing is
  // separate from placing: the setTempo tool inserts change events, and this
  // retunes whichever one the playhead sits under. Returns whether anything
  // changed. See setTimeSignatureAtBeat / setKeySignatureAtBeat.
  setTempoAtBeat = (beat: number, microsecondsPerBeat: number) =>
    e := @tempoEventAtBeat beat
    return false if e.microsecondsPerBeat is microsecondsPerBeat

    e.microsecondsPerBeat = microsecondsPerBeat
    @tempoMapChanged.increment()

    true

  tempoMapChanged = Observable 0

  // [{beat, numerator, denominator}]
  timeSignatures = =>
    @timeSignatureMap

  // Insert a time signature change event at this beat
  addTimeSignature = (beat: number) =>
    tick := floor beat * @ticksPerBeat

    existingEvent := @timeSignatureEventAtTick(tick)
    if existingEvent.tick is tick
      return false

    e := new MidiEvent tick,
      new Uint8Array [0xff, 0x58, 0x04, 0, 0, 24, 8]

    // Initialize it to the value of the existing time signature
    {numerator, denominator} := existingEvent or defaultTimeSignature

    e.numerator = numerator
    e.denominator = denominator

    @_addTimeSignature(e)

    @events.push e
    @sortEvents()

    @timeSignatureMapChanged.increment()

    return true

  timeSignatureEventAtBeat = (beat: number) =>
    @timeSignatureEventAtTick beat * @ticksPerBeat

  timeSignatureEventAtTick = (tick: number) =>
    mappedEventAtTick(@timeSignatureMap, tick)

  // Set the time signature in force at `beat`. See setTempoAtBeat.
  setTimeSignatureAtBeat = (beat: number, numerator: number, denominator: number) =>
    e := @timeSignatureEventAtBeat beat
    return false if e.numerator is numerator and e.denominator is denominator

    // `set` writes through to the underlying event and increments
    // timeSignatureMapChanged.
    e.set numerator, denominator

    true

  timeSignatureMapChanged = Observable 0

  keySignatures = =>
    @keySignatureMap

  addKeySignature = (beat: number) =>
    tick := floor beat * @ticksPerBeat

    existingEvent := @keySignatureEventAtTick(tick)
    if existingEvent.tick is tick
      return false

    e := new MidiEvent tick,
      new Uint8Array [0xff, 0x59, 0x02, 0, 0]

    // Initialize it to the value of the existing key signature
    {key, scale} := existingEvent

    e.key = key
    e.scale = scale

    @keySignatureMap.push e
    @keySignatureMap.sort byTick

    @events.push e
    @sortEvents()

    @keySignatureMapChanged.increment()

    return true

  keySignatureEventAtBeat = (beat: number) =>
    @keySignatureEventAtTick beat * @ticksPerBeat

  keySignatureEventAtTick = (tick: number) =>
    mappedEventAtTick(@keySignatureMap, tick)

  // Set the key signature in force at `beat`. See setTempoAtBeat.
  setKeySignatureAtBeat = (beat: number, key: number, scale: number) =>
    e := @keySignatureEventAtBeat beat
    return false if e.key is key and e.scale is scale

    e.key = key
    e.scale = scale
    @keySignatureMapChanged.increment()

    true

  keySignatureMapChanged = Observable 0

  updatePlayerMetaEvent = =>
    // Technically this is utf-8 and may be weird if there are bytes > 127
    // but will probably work for us and most other MIDI players
    asciiBytes := asciiEncoder.encode(JSON.stringify(@playerMeta))
    len := asciiBytes.length

    maxLen := len + 6

    buffer := new ArrayBuffer maxLen
    dataStream := new DataStream buffer

    dataStream.putUint8(0xff)
    dataStream.putUint8(0x01)
    dataStream.putVarUint(len)
    dataStream.putBytes(asciiBytes)

    data := dataStream.bytes()

    if @playerMetaEvent
      @playerMetaEvent.data = data
    else
      @playerMetaEvent = new MidiEvent 0, data
      @events.unshift @playerMetaEvent

  // returns byte array containing MIDI data
  toMidi = =>
    // Hack: the events should already be sorted before this point, but if
    // there is an active selection being moved they won't be.
    @sortEvents()

    // Make sure metadata event is up to date
    @updatePlayerMetaEvent()

    currentTick .= 0
    trackSizeInBytes .= @events.map (e) =>
      // Skip endOfTrack messages
      return 0 if e.type is "endOfTrack"

      {data, tick} := e
      let deltaTime
      if tick >= currentTick
        deltaTime = tick - currentTick
        currentTick = tick
      else
        throw new Error "Negative deltaTime"

      return data.byteLength + deltaTimeByteLength(deltaTime)!
    .reduce (a, b) =>
      a + b
    , 0

    // EOT message
    deltaTime .= max 0, @tickLength() - currentTick
    trackSizeInBytes += 3 + deltaTimeByteLength(deltaTime)!

    totalSizeInBytes := trackSizeInBytes + 24

    buffer := new ArrayBuffer totalSizeInBytes
    dataStream := new DataStream buffer

    trackCount := 1

    // Write header
    dataStream.putAscii("MThd")
    dataStream.putUint32(6) // 6 length header
    dataStream.putUint16(1) // Type 1
    dataStream.putUint16(trackCount)
    dataStream.putUint16(@ticksPerBeat & 0x7fff)

    // Write track
    dataStream.putAscii("MTrk")
    dataStream.putUint32(trackSizeInBytes)

    currentTick = 0
    @events.forEach (e) =>
      // Skip endOfTrack messages
      return if e.type is "endOfTrack"

      {data, tick} := e
      let deltaTime
      if tick >= currentTick
        deltaTime = tick - currentTick
        currentTick = tick
      else
        throw new Error "Negative deltaTime"

      dataStream.putVarUint(deltaTime)
      dataStream.putBytes(data)

    // Write endOfTrack event
    deltaTime = max 0, @tickLength() - currentTick
    dataStream.putVarUint(deltaTime)
    dataStream.putBytes([0xff, 0x2f, 0x00])

    return dataStream.bytes()

  toBlob = =>
    new Blob [@toMidi()],
      type: "audio/midi"

  debugInfo = =>
    events: @events.map (e) =>
      tick: e.tick
      data: Array.from(e.data)
    playerMeta: @playerMeta
    tempoMap: @tempoMap
    ticksPerBeat: @ticksPerBeat
    timeSignatures: @timeSignatureMap

  @()
    // Create observable methods to track metadata json event values
    metadataKeys.forEach (key) =>
      mapMetadata @, @playerMeta, key

    // update measure numbers when time signatures change
    @timeSignatureMapChanged.observe =>
      @measureNumbers.length = 0
      calculateMeasureNumbers(@measureNumbers, @timeSignatureMap, @beatLength())

    @resetMetadata()
    @ensureMetadata()

deltaTimeByteLength := (n: number) =>
  if n < 0x80
    1
  else if n < (0x80 << 7)
    2
  else if n < (0x80 << 14)
    3
  else if n < (0x80 << 21)
    4
  else if n < (0x80 << 28)
    5

export default MidiSong

export type StateData = ReturnType<MidiSong["getState"]>
